import React from "react";
import { Route, Redirect } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { NOT_FOUND_PATH } from "configs/AppConfig";

/**
 * RoleBasedRouting - Route guard component that protects routes based on permissions
 * 
 * @param {Array|string} AuthNeeded - Single permission or array of permissions (user needs any one)
 * @param {boolean} exactAuth - If true, user must have exact permission match (legacy support)
 * @param {boolean} requireAll - If true, user must have ALL permissions (default: false, needs any)
 * @param {Object} ...rest - All other Route props (path, component, etc.)
 */
export const RoleBasedRouting = (props) => {
  const { exactAuth, AuthNeeded, requireAll = false, ...rest } = props;

  // Normalize AuthNeeded to always be an array
  const requiredPermissions = Array.isArray(AuthNeeded) ? AuthNeeded : [AuthNeeded];

  // Use the new permission-based auth hook
  const { hasPermission, hasAllPermissions, hasExactPermission, isMaster } = useAuth([]);

  // Determine if user has access
  let hasAccess = false;

  if (exactAuth) {
    // Legacy exact match mode - user must have the exact permission
    if (requiredPermissions.length === 1) {
      hasAccess = hasExactPermission(requiredPermissions[0]);
    } else {
      // For multiple permissions in exact mode, check if user has any of them
      hasAccess = hasPermission(requiredPermissions);
    }
  } else if (requireAll) {
    // User must have ALL permissions
    hasAccess = hasAllPermissions(requiredPermissions);
  } else {
    // Default: User needs ANY one of the permissions
    hasAccess = hasPermission(requiredPermissions);
  }

  if (hasAccess) {
    return <Route {...rest}>{props.children}</Route>;
  } else {
    return (
      <Route>
        <Redirect to={NOT_FOUND_PATH} from={props.location?.pathname} />
      </Route>
    );
  }
};

export default RoleBasedRouting;
